import { captureOpsFailure } from '../shared/sentry/sentryOps';
export type ExternalServiceType = 'carga_virtual' | 'sube';

export type ExternalServicePaymentMethod = 'cash' | 'transfer';

export type ExternalServiceOperationRow = {
  id: string;
  branchId: string;
  type: ExternalServiceType;
  amount: number;
  reference: string | null;
  createdAt: string;
  synced: number;
  paymentMethod: ExternalServicePaymentMethod | null;
  cashSessionId: string | null;
};

export const insertExternalServiceOperation = async (params: {
  branchId: string;
  type: ExternalServiceType;
  amount: number;
  reference?: string | null;
  paymentMethod?: ExternalServicePaymentMethod | null;
  cashSessionId?: string | null;
}): Promise<string> => {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const paymentMethod = params.paymentMethod ?? null;
  const cashSessionId = params.cashSessionId ?? null;

    const { submitCommand } = await import('../sync/core/submitCommand');
        const result = await submitCommand({
          entity: 'external_service',
          operation: 'insert',
          payload: {
            operationId: id,
            id,
            branchId: params.branchId,
            type: params.type,
            amount: params.amount,
            reference: params.reference ?? null,
            createdAt: now,
            paymentMethod,
            cashSessionId,
          },
        });
        if (!result.applied) {
          const error = new Error(
            result.ingestError?.trim() || 'No se pudo registrar la recarga en el servidor. Reintentá.'
          );
          captureOpsFailure({
            module: 'pos',
            op: 'pos.recarga',
            error,
            ingestError: result.ingestError,
            applied: false,
            path: 'web-ram',
            itemId: id,
            extra: { operationId: id, branchId: params.branchId, type: params.type },
          });
          throw error;
        }
        return id;
};

export type ExternalServiceOperationsSummary = {
  total: number;
  count: number;
};

export const getExternalServiceOperationsSummary = async (params: {
  branchId: string;
  fromIso: string;
  toIso: string;
}): Promise<ExternalServiceOperationsSummary> => {
  const { supabase } = await import('../shared/supabase/supabaseClient');
  const { data, error } = await supabase
    .schema('integrations')
    .rpc('get_external_service_operations_summary', {
      p_branch_id: params.branchId,
      p_from: params.fromIso,
      p_to: params.toIso,
    });
  if (error) throw error;
  const row = (data ?? [])[0] as
    | { total_amount: number | string; ops_count: number | string }
    | undefined;
  return { total: Number(row?.total_amount ?? 0), count: Number(row?.ops_count ?? 0) };
};

export const getExternalServiceOperations = async (params: {
  branchId: string;
  limit?: number;
  offset?: number;
}): Promise<ExternalServiceOperationRow[]> => {
  const { supabase } = await import('../shared/supabase/supabaseClient');
  const { data, error } = await supabase
    .schema('integrations')
    .rpc('list_external_service_operations', {
      p_branch_id: params.branchId,
      p_limit: params.limit ?? 50,
      p_offset: params.offset ?? 0,
    });
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: r.id,
    branchId: r.branch_id,
    type: r.type as ExternalServiceType,
    amount: Number(r.amount),
    reference: r.reference,
    createdAt: r.created_at,
    synced: 1, // ya viene del server
    paymentMethod: (r.payment_method as ExternalServicePaymentMethod | null) ?? null,
    cashSessionId: r.cash_session_id ?? null,
  }));
};
